/**
 * Which Machanicdesk export a file is, read from its own header row.
 *
 * <p>A MIRROR of the server's {@code MachanicdeskExport.detect}, and the server stays
 * the authority: this exists so a file dropped on the wrong slot is refused at once,
 * naming what it actually is, rather than queued and refused minutes later in the log.
 * If the two ever disagree, the server's answer is the one that counts.
 *
 * <h2>⚠️ The order of the checks is load-bearing</h2>
 *
 * These files share columns. Invoice Items and Invoices Summary both carry an
 * {@code Invoice Number} AND a {@code Job Number}, and the jobs export carries an
 * {@code Invoice Number} too — so "Invoice Number" alone decides nothing, and checking
 * for "Job Number" first would read a sheet of invoice lines as jobs. Each export is
 * identified by a column the others do NOT have: a priced line has {@code Unit Price},
 * only the summary tracks {@code Amount Due}, and payments and credit notes have their
 * own number.
 *
 * <p>The Customers sheet has a {@code Contact Name} column of its own, so only the
 * fuller contact columns tell the two customer tabs apart.
 */

import { splitRecords, findHeaderDepth, stripBom } from './splitCsv';

/** Present only on the Customer Contacts sheet — never on Customers. */
const CONTACT_ONLY = [
    'Contact First Name', 'Contact Last Name',
    'Contact Mobile', 'Contact Phone', 'Contact Email',
];

/**
 * How much of the file is read to find the header. The header sits in the first two
 * lines of every export; 64 KB is thousands of times that and still nothing against
 * a 9 MB part.
 */
export const HEADER_PROBE_BYTES = 64 * 1024;

/**
 * @param {File|Blob} file
 * @returns {Promise<string|null>} the export kind, or null when it is none of them
 */
export async function detectExport(file) {
    const text = await file.slice(0, HEADER_PROBE_BYTES).text();
    return detectExportFromText(text);
}

/** The pure half, so the rules can be tested without a File. */
export function detectExportFromText(text) {
    const columns = headerColumns(stripBom(text ?? ''));
    const has = (name) => columns.includes(name);

    if (has('Credit Note Number')) return 'CREDIT_NOTES';
    if (has('Payment Number')) return 'INVOICE_PAYMENTS';
    if (has('Quote Number')) return has('Unit Price') ? 'QUOTE_ITEMS' : 'QUOTES';
    if (has('Invoice Number') && has('Unit Price')) return 'INVOICE_ITEMS';
    if (has('Invoice Number') && has('Amount Due')) return 'INVOICES';
    // Jobs next: that file has a Customer ID column too.
    if (has('Job Number')) return 'JOBS';
    if (has('Customer ID')) {
        return CONTACT_ONLY.some(has) ? 'CUSTOMER_CONTACTS' : 'CUSTOMERS';
    }
    return null;
}

/**
 * The header row's column names.
 *
 * The first record with more than one field, past the "Table 1" title line the
 * exports open with — the same reading the splitter uses, so the two can never
 * disagree about where the header is.
 */
export function headerColumns(text) {
    const records = splitRecords(text);
    if (records.length === 0) {
        return [];
    }
    return splitFields(records[findHeaderDepth(records)]).map((c) => c.trim());
}

/** One record into its fields, honouring quotes. */
function splitFields(record) {
    const fields = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < record.length; i++) {
        const char = record[i];
        if (char === '"') {
            if (inQuotes && record[i + 1] === '"') {
                current += '"';
                i++;
                continue;
            }
            inQuotes = !inQuotes;
            continue;
        }
        if (!inQuotes && char === ',') {
            fields.push(current);
            current = '';
            continue;
        }
        current += char;
    }
    fields.push(current);
    return fields;
}
